import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import type { Column } from "@/components/DataTable";
import { Button } from "@/components/ui/button";

/** The accessor a column exports with, if any (csvValue, else sortValue). */
function csvAccessor<T>(col: Column<T>) {
  return col.csvValue ?? col.sortValue;
}

/** RFC 4180: quote fields containing commas, quotes, or newlines. */
function csvEscape(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadCsv<T>(name: string, columns: Column<T>[], rows: T[]): void {
  const lines = [
    columns.map((c) => csvEscape(c.csvHeader ?? c.header)).join(","),
    ...rows.map((row) =>
      columns
        .map((c) => {
          const v = csvAccessor(c)!(row);
          return csvEscape(v == null ? "" : String(v));
        })
        .join(","),
    ),
  ];
  // BOM so Excel decodes UTF-8 instead of guessing the system codepage.
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Data-handling reminder shown before every CSV download (per CRO/CTO
 * policy): confirm keeps the data internal, cancel aborts the export.
 */
function ConfirmExportDialog({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onCancel}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="csv-confirm-title"
        aria-describedby="csv-confirm-body"
        className="w-full max-w-md rounded-lg border bg-card p-6 text-card-foreground shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="csv-confirm-title" className="text-base font-semibold">
          Internal data — handle with care
        </h2>
        <p id="csv-confirm-body" className="mt-2 text-sm text-muted-foreground">
          This export contains internal business data. Don't share it with
          external people or tools unless it's been cleared with the CRO or
          CTO.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" autoFocus onClick={onConfirm}>
            <Download className="h-3.5 w-3.5" aria-hidden />
            Download CSV
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * CSV export button for a table card's title row. Exports the columns'
 * `csvValue ?? sortValue` accessors over `rows` as given (the page's
 * filtered, default-ordered rows), behind the data-handling confirmation.
 * Renders nothing when no column is exportable.
 */
export function CsvDownloadButton<T>({
  name,
  columns,
  rows,
}: {
  /** Filename stem, e.g. "store-status" → store-status-2026-08-12.csv */
  name: string;
  columns: Column<T>[];
  rows: T[];
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const csvColumns = columns.filter(csvAccessor);
  if (csvColumns.length === 0) return null;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
        <Download className="h-3.5 w-3.5" aria-hidden />
        Download CSV
      </Button>
      {confirmOpen ? (
        <ConfirmExportDialog
          onCancel={() => setConfirmOpen(false)}
          onConfirm={() => {
            setConfirmOpen(false);
            downloadCsv(name, csvColumns, rows);
          }}
        />
      ) : null}
    </>
  );
}
